import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Loader2, Undo2, CheckCircle2, XCircle, Send, ExternalLink, Copy, History,
} from 'lucide-react';

const STATUS_COLOR = {
  draft:              'text-cyan-sys border-cyan-sys',
  approved:           'text-hold border-hold',
  rejected:           'text-sell border-sell',
  dispatched:         'text-cyan-sys border-cyan-sys',
  completed_success:  'text-buy border-buy',
  completed_failed:   'text-sell border-sell',
};

/**
 * RollbackManagerSection — Phase 51.G Lab section.
 * Operator picks a previous successful production SHA → 4-eyes flow
 * fires `rollback.sh <sha>` via GitHub workflow_dispatch (if configured)
 * or surfaces the manual command. Always production-gated.
 */
export default function RollbackManagerSection({ isProd }) {
  const [requests, setRequests]   = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [targetSha, setTargetSha] = useState('');
  const [reason, setReason]       = useState('');
  const [busy, setBusy]           = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = () =>
    api.get('/lab/rollback/list?limit=50')
      .then(({ data }) => setRequests(data.requests || []))
      .catch(() => {});

  useEffect(() => {
    load();
    api.get('/lab/rollback/candidates?limit=20')
      .then(({ data }) => setCandidates(data.candidates || []))
      .catch(() => {});
  }, []);

  const create = async () => {
    if (!targetSha.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/lab/rollback/request', {
        target_sha: targetSha.trim(),
        reason,
      });
      toast.success('Rollback ticket opened');
      setSelectedId(data.id);
      setTargetSha('');
      setReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Open failed');
    } finally {
      setBusy(false);
    }
  };

  const doAction = async (id, kind) => {
    setActionBusy({ id, kind });
    try {
      await api.post(`/lab/rollback/${id}/${kind}`);
      toast.success(`Ticket ${kind}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${kind} failed`);
    } finally {
      setActionBusy(null);
    }
  };

  const copyCmd = (cmd) => {
    if (!cmd) return;
    navigator.clipboard.writeText(cmd).then(() => toast.success('Command copied'));
  };

  const selected = useMemo(
    () => requests.find((r) => r.id === selectedId),
    [requests, selectedId],
  );

  return (
    <div data-testid="lab-rollback" className="space-y-3">
      {/* OPEN TICKET */}
      <div className="border border-cyan-sys p-3">
        <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">
          // open rollback ticket
        </div>
        <div className="grid md:grid-cols-3 gap-2">
          <select
            data-testid="lab-rollback-candidate-select"
            value={targetSha}
            onChange={(e) => setTargetSha(e.target.value)}
            disabled={!isProd || busy}
            className="bg-transparent border border-[var(--border-default)] px-2 py-2 text-[11px] font-mono focus:border-cyan-sys outline-none"
          >
            <option value="">— pick recent successful sha —</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.sha}>
                {c.sha} · {(c.deployed_at || '').slice(0, 19).replace('T', ' ')}Z
              </option>
            ))}
          </select>
          <input
            data-testid="lab-rollback-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="reason (incident #, metric regression, etc.)"
            disabled={!isProd || busy}
            className="md:col-span-2 bg-transparent border border-[var(--border-default)] px-2 py-2 text-[11px] font-mono focus:border-cyan-sys outline-none"
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="text-[10px] text-[var(--txt-secondary)]">
            {isProd
              ? `4-eyes rollback flow. Candidates list shows the ${candidates.length} most recent successful deploys.`
              : '⚠ Rollback Manager is production-only. The list below is read-only here.'}
          </div>
          <button
            data-testid="lab-rollback-submit"
            onClick={create}
            disabled={!isProd || busy || !targetSha.trim()}
            className="flex items-center gap-1.5 border border-sell text-sell px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-sell hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            {busy ? 'opening...' : 'open ticket'}
          </button>
        </div>
      </div>

      {/* CANDIDATES SUMMARY */}
      {candidates.length > 0 && (
        <div className="border border-[var(--border-default)] p-2">
          <div className="text-[9px] tracking-[0.25em] uppercase text-[var(--txt-secondary)] mb-1 flex items-center gap-1.5">
            <History size={10} /> recent successful deploys
          </div>
          <div className="flex flex-wrap gap-1.5">
            {candidates.slice(0, 8).map((c) => (
              <button
                key={c.id}
                onClick={() => setTargetSha(c.sha)}
                data-testid={`lab-rollback-candidate-${c.sha}`}
                className="text-[10px] font-mono border border-[var(--border-default)] hover:border-cyan-sys px-1.5 py-0.5 text-[var(--txt-primary)]"
                title={c.deployed_at}
              >
                {c.sha}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* LIST + DETAIL */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--txt-secondary)] mb-1">
            {requests.length} rollback ticket(s)
          </div>
          {requests.length === 0 ? (
            <div className="border border-[var(--border-default)] p-4 text-[11px] text-[var(--txt-secondary)]">
              No rollback tickets yet. Open one above when a deploy needs to be reverted.
            </div>
          ) : (
            requests.map((r) => (
              <button
                key={r.id}
                data-testid={`lab-rollback-${r.id}`}
                onClick={() => setSelectedId(r.id)}
                className={`w-full text-left border p-2.5 transition-colors ${
                  r.id === selectedId
                    ? 'border-cyan-sys bg-[var(--bg-panel-lighter)]'
                    : 'border-[var(--border-default)] hover:border-cyan-sys'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[11px] text-[var(--txt-primary)] truncate font-mono">
                    {r.id.slice(0, 8)} → {(r.target_sha || '').slice(0, 12)}
                  </span>
                  <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border ${STATUS_COLOR[r.status] || 'text-[var(--txt-muted)] border-[var(--border-default)]'}`}>
                    {r.status}
                  </span>
                </div>
                <div className="text-[9px] text-[var(--txt-secondary)] truncate">
                  {(r.created_at || '').slice(0, 19).replace('T', ' ')}Z ·
                  {' '}by {r.requested_by}
                  {r.reason && <> · {r.reason.slice(0, 40)}</>}
                </div>
              </button>
            ))
          )}
        </div>

        {/* DETAIL */}
        <div className="border border-[var(--border-default)] p-3 min-h-[420px]">
          {!selected ? (
            <div className="text-[11px] text-[var(--txt-secondary)]">Select a ticket to inspect.</div>
          ) : (
            <div className="space-y-3 text-[11px]">
              <div className="flex items-center justify-between">
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys">// rollback ticket</div>
                <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border ${STATUS_COLOR[selected.status] || ''}`}>
                  {selected.status}
                </span>
              </div>

              <KV k="ticket"     v={selected.id} />
              <KV k="target_sha" v={selected.target_sha} color="text-sell" />
              <KV k="requested"  v={`${selected.requested_by} · ${(selected.created_at || '').slice(0, 19).replace('T', ' ')}Z`} />
              {selected.approved_by && <KV k="approver" v={selected.approved_by} />}
              {selected.dispatched_by && <KV k="dispatcher" v={selected.dispatched_by} />}

              {selected.reason && (
                <div>
                  <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-0.5">// reason</div>
                  <div className="text-[var(--txt-primary)] whitespace-pre-wrap">{selected.reason}</div>
                </div>
              )}

              {selected.workflow_run_url && (
                <a
                  href={selected.workflow_run_url}
                  target="_blank" rel="noreferrer"
                  data-testid="lab-rollback-workflow-link"
                  className="inline-flex items-center gap-1.5 text-cyan-sys hover:underline text-[11px]"
                >
                  <ExternalLink size={11} /> GitHub Actions run
                </a>
              )}

              {selected.manual_command && (
                <div>
                  <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-0.5 flex items-center justify-between">
                    <span>// manual dispatch command</span>
                    <button
                      data-testid="lab-rollback-copy-cmd"
                      onClick={() => copyCmd(selected.manual_command)}
                      className="text-cyan-sys hover:text-buy flex items-center gap-1 text-[10px]"
                    >
                      <Copy size={10} /> copy
                    </button>
                  </div>
                  <pre className="p-2 text-[10px] font-mono text-sell whitespace-pre overflow-x-auto border border-[var(--border-default)] bg-[var(--bg-panel-lighter)]">
                    {selected.manual_command}
                  </pre>
                </div>
              )}

              {selected.dispatch_error && (
                <div className="border border-hold text-hold p-2 text-[10px] font-mono">
                  dispatch fallback: {selected.dispatch_error}
                </div>
              )}

              {/* ACTIONS */}
              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[var(--border-default)]">
                {selected.status === 'draft' && (
                  <>
                    <ActionBtn
                      testid="lab-rollback-approve" label="approve" icon={CheckCircle2}
                      color="buy"
                      busy={actionBusy?.id === selected.id && actionBusy.kind === 'approve'}
                      disabled={!isProd}
                      onClick={() => doAction(selected.id, 'approve')}
                    />
                    <ActionBtn
                      testid="lab-rollback-reject" label="reject" icon={XCircle}
                      color="sell"
                      busy={actionBusy?.id === selected.id && actionBusy.kind === 'reject'}
                      disabled={!isProd}
                      onClick={() => doAction(selected.id, 'reject')}
                    />
                  </>
                )}
                {selected.status === 'approved' && (
                  <ActionBtn
                    testid="lab-rollback-dispatch" label="dispatch rollback" icon={Undo2}
                    color="sell"
                    busy={actionBusy?.id === selected.id && actionBusy.kind === 'dispatch'}
                    disabled={!isProd}
                    onClick={() => doAction(selected.id, 'dispatch')}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ k, v, color }) {
  return (
    <div className="flex items-baseline gap-2 text-[10px] font-mono">
      <span className="text-[var(--txt-secondary)] uppercase tracking-[0.15em] min-w-[80px]">
        {k}
      </span>
      <span className={`text-[var(--txt-primary)] break-all ${color || ''}`}>{v}</span>
    </div>
  );
}

function ActionBtn({ testid, label, icon: Icon, color, busy, disabled, onClick }) {
  const COLOR = {
    buy:        'border-buy text-buy hover:bg-buy hover:text-black',
    sell:       'border-sell text-sell hover:bg-sell hover:text-black',
    'cyan-sys': 'border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black',
  }[color] || 'border-[var(--border-default)] text-[var(--txt-primary)]';
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled || busy}
      className={`flex items-center gap-1 border px-2 py-1 text-[10px] uppercase tracking-[0.2em] disabled:opacity-30 disabled:cursor-not-allowed ${COLOR}`}
    >
      {busy ? <Loader2 size={11} className="animate-spin" /> : <Icon size={11} />}
      {label}
    </button>
  );
}
